import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@car/ui/components/alert-dialog";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Calendar } from "@car/ui/components/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@car/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import {
  FormAlert,
  FormField,
  statusControlClass,
  useAppForm,
  useStore,
} from "@car/ui/components/form";
import { Popover, PopoverContent, PopoverTrigger } from "@car/ui/components/popover";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@car/ui/components/table";
import { FileDropzone, UploadList, useUploads } from "@car/ui/components/file-upload";
import { Image } from "@car/ui/components/image";
import { intText, optionalText, parseIntText, requiredChoice } from "@car/ui/lib/form-rules";
import { formatBytes } from "@car/ui/lib/upload";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  Attachment01Icon,
  Calendar03Icon,
  Car03Icon,
  Delete02Icon,
  Edit02Icon,
  Folder01Icon,
  GaugeIcon,
  Image01Icon,
  LicenseIcon,
  Pdf01Icon,
  PlusSignIcon,
  SecurityCheckIcon,
  Shield01Icon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  OrderDocuments,
  PageHeader,
  PanelEmpty,
  PanelQueryError,
  Plate,
  RepairStatusBadge,
  WarrantyBadge,
} from "@/components/panel-bits";
import { SharingSection } from "@/components/vehicle-sharing";
import { useSiteContent } from "@/lib/content";
import { faDate, faKm, faNumber, faPlainNumber, faToman } from "@/lib/format";
import {
  documentFilesPerVehicle,
  documentTypeOptions,
  garageName,
  serviceTypeLabels,
  type DocumentFile,
  type DocumentState,
  type DocumentTypeOption,
  type MaintenanceDue,
  type VehicleDocument,
} from "@/lib/panel";
import { uploadPolicy } from "@/lib/uploads";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/vehicles/$ownedId")({
  head: () => ({ meta: [{ title: "جزئیات خودرو | پنل مشتریان تابان خودرو" }] }),
  component: VehicleDetailPage,
});

const dueBadge = (due: MaintenanceDue) => {
  if (due.status === "overdue") return <Badge variant="destructive">موعد گذشته</Badge>;
  if (due.status === "soon")
    return <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-400">نزدیک</Badge>;
  return <Badge variant="outline">به‌روز</Badge>;
};

/** نوع‌هایی که کارشناس در مدیریت محتوا اضافه می‌کند آیکن پیش‌فرض می‌گیرند */
const coreDocumentIcons = new Map<string, IconSvgElement>([
  ["card", LicenseIcon],
  ["insurance", Shield01Icon],
  ["inspection", SecurityCheckIcon],
]);

const documentStateBadge = (state: DocumentState) => {
  if (state === "expired") return <Badge variant="destructive">منقضی شده</Badge>;
  if (state === "ending")
    return (
      <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-400">رو به پایان</Badge>
    );
  if (state === "permanent") return <Badge variant="outline">بدون انقضا</Badge>;
  return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">معتبر</Badge>;
};

function VehicleDetailPage() {
  const { ownedId } = Route.useParams();
  const query = useQuery(orpc.panel.garage.byId.queryOptions({ input: { id: ownedId } }));

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-56 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
      </div>
    );
  }

  if (query.isError) {
    return <PanelQueryError title="اطلاعات خودرو بارگیری نشد" query={query} />;
  }

  if (!query.data) {
    return (
      <PanelEmpty
        icon={Car03Icon}
        title="خودرو پیدا نشد"
        description="این خودرو در فهرست خودروهای شما نیست."
        action={
          <Button variant="outline" nativeButton={false} render={<Link to="/panel/vehicles" />}>
            بازگشت به خودروهای من
          </Button>
        }
      />
    );
  }

  const {
    garage,
    access,
    documents,
    files,
    purchaseDocuments,
    services,
    repairOrders,
    appointments,
  } = query.data;
  const canBook = !access || access.canBook;

  return (
    <div className="grid gap-6">
      <PageHeader
        title={`${garageName(garage)} مدل ${faPlainNumber(garage.owned.modelYear)}`}
        description={
          access
            ? `مشترک از طرف ${access.ownerName || "مالک"}؛ ${access.canBook ? "می‌توانید برایش نوبت و امداد بگیرید." : "فقط مشاهده."}`
            : garage.vehicle.trim
              ? `تیپ ${garage.vehicle.trim}`
              : undefined
        }
        action={
          canBook ? (
            <Button
              nativeButton={false}
              render={<Link to="/panel/appointments/new" search={{ vehicle: garage.owned.id }} />}
            >
              رزرو نوبت برای این خودرو
            </Button>
          ) : undefined
        }
      />

      <section className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <Card className={garage.vehicle.image ? "overflow-hidden pt-0" : ""}>
          {garage.vehicle.image ? (
            <Image
              src={garage.vehicle.image}
              alt={garage.vehicle.name}
              width={800}
              height={288}
              sizes="(max-width: 1024px) 100vw, 40vw"
              preload
              className="h-48 w-full object-cover"
            />
          ) : null}
          <CardContent className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Plate plate={garage.owned.plate} text={garage.owned.plateText} />
              <WarrantyBadge warranty={garage.warranty} />
            </div>
            <dl className="grid gap-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">شماره شاسی (VIN)</dt>
                <dd dir="ltr" className="font-mono text-xs font-bold tracking-wider">
                  {garage.owned.vin || "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">تاریخ خرید</dt>
                <dd className="font-bold">{faDate(garage.owned.purchaseDateIso)}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">پوشش گارانتی</dt>
                <dd className="font-bold">
                  {garage.warranty.state === "none"
                    ? "ندارد"
                    : `${faNumber(garage.owned.warrantyYears)} سال یا ${faKm(garage.owned.warrantyKm)}`}
                </dd>
              </div>
              {garage.warranty.state === "none" || !garage.warranty.byDate ? null : (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-muted-foreground">پایان گارانتی</dt>
                  <dd className="font-bold">
                    {faDate(garage.warranty.endDateIso)}
                    {garage.warranty.state !== "expired"
                      ? ` (${faNumber(garage.warranty.monthsLeft)} ماه دیگر)`
                      : ""}
                  </dd>
                </div>
              )}
            </dl>
            {garage.warranty.kmEstimated && !access ? (
              <p className="rounded-lg bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-400">
                کارکرد تخمینی احتمالاً نزدیک سقف کیلومتر گارانتی است؛ پایان گارانتی فقط با کیلومتر
                ثبت‌شده اعلام می‌شود. کیلومتر فعلی خودرو را ثبت کنید.
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <HugeiconsIcon icon={GaugeIcon} strokeWidth={1.8} className="size-5" />
                کارکرد و برنامه سرویس
              </CardTitle>
              {access ? null : (
                <AddReadingDialog ownedId={garage.owned.id} lastKm={garage.lastReading.km} />
              )}
            </div>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg bg-muted/60 p-3">
                <div className="text-xs text-muted-foreground">آخرین کیلومتر ثبت‌شده</div>
                <div className="mt-1 font-black">{faKm(garage.lastReading.km)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {faDate(garage.lastReading.dateIso)}
                </div>
                {garage.removableReadingId && !access ? (
                  <RemoveReadingButton
                    ownedId={garage.owned.id}
                    readingId={garage.removableReadingId}
                    km={garage.lastReading.km}
                  />
                ) : null}
              </div>
              <div className="rounded-lg bg-muted/60 p-3">
                <div className="text-xs text-muted-foreground">کارکرد تخمینی امروز</div>
                <div className="mt-1 font-black">{faKm(garage.estimatedKm)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{garage.estimateNote}</div>
              </div>
              <div className="rounded-lg bg-muted/60 p-3">
                <div className="text-xs text-muted-foreground">میانگین پیمایش ماهانه</div>
                <div className="mt-1 font-black">{faKm(garage.monthlyRate)}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  از دو قرائت آخر؛ روز خرید صفر کیلومتر حساب می‌شود
                </div>
              </div>
            </div>

            {garage.maintenance.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                برنامه سرویس دوره‌ای این مدل هنوز ثبت نشده است.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>آیتم سرویس</TableHead>
                      <TableHead>موعد بعدی</TableHead>
                      <TableHead>تا موعد</TableHead>
                      <TableHead>وضعیت</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {garage.maintenance.map((due) => (
                      <TableRow key={due.item.id}>
                        <TableCell className="font-bold">{due.item.title}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {faKm(due.dueKm)}
                          {due.dueDateIso ? ` یا ${faDate(due.dueDateIso)}` : ""}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{due.text}</TableCell>
                        <TableCell>{dueBadge(due)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {access ? null : (
        <section aria-label="مدارک خودرو">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">مدارک خودرو</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <DocumentDialog ownedId={garage.owned.id} />
                  <VehicleFilesDialog ownedId={garage.owned.id} files={files} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  کارت خودرو، بیمه‌نامه و معاینه فنی را ثبت کنید تا پیش از انقضا یادآوری بگیرید.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {documents.map((document) => (
                    <DocumentCard key={document.id} ownedId={garage.owned.id} document={document} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {purchaseDocuments ? (
        <section aria-label="مدارک خرید و ترخیص">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="grid gap-1">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <HugeiconsIcon icon={Folder01Icon} strokeWidth={1.8} className="size-5" />
                    مدارک خرید و ترخیص
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    اسنادی که در مراحل سفارش {purchaseDocuments.orderNumber} برای این خودرو صادر شد.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  nativeButton={false}
                  render={<Link to="/panel/orders" hash={`order-${purchaseDocuments.orderId}`} />}
                >
                  مشاهده سفارش
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <OrderDocuments
                orderId={purchaseDocuments.orderId}
                files={purchaseDocuments.files}
                emptyText="سندی از مراحل خرید این خودرو در دسترس شما قرار نگرفته است."
              />
            </CardContent>
          </Card>
        </section>
      ) : null}

      {repairOrders.length > 0 || appointments.length > 0 ? (
        <section className="grid gap-4 lg:grid-cols-2" aria-label="موارد جاری">
          {repairOrders.map((order) => (
            <Card key={order.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base">{order.title}</CardTitle>
                  <RepairStatusBadge status={order.status} />
                </div>
              </CardHeader>
              <CardContent className="grid gap-1 text-sm text-muted-foreground">
                {order.note ? <p>{order.note}</p> : null}
                <p>
                  شماره پذیرش {faPlainNumber(order.number)}
                  {SEP}
                  {order.branchName}
                  {order.estimatedReadyIso
                    ? `${SEP}تحویل تقریبی ${faDate(order.estimatedReadyIso)}`
                    : ""}
                </p>
              </CardContent>
            </Card>
          ))}
          {appointments.map((appointment) => (
            <Card key={appointment.id}>
              <CardHeader>
                <CardTitle className="text-base">نوبت رزروشده</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-1 text-sm text-muted-foreground">
                <p className="font-bold text-foreground">
                  {serviceTypeLabels[appointment.serviceType]}
                </p>
                <p>
                  {faDate(appointment.dateIso)}، ساعت {appointment.slot}
                  {SEP}
                  {appointment.branchName}
                </p>
              </CardContent>
            </Card>
          ))}
        </section>
      ) : null}

      {access ? null : (
        <SharingSection ownedId={garage.owned.id} vehicleName={garage.vehicle.name} />
      )}

      {access ? null : (
        <section aria-label="سوابق خدمات این خودرو">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">سوابق خدمات و تعمیرات</CardTitle>
            </CardHeader>
            <CardContent>
              {services.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  هنوز سرویسی برای این خودرو ثبت نشده است.
                </p>
              ) : (
                <ol className="grid gap-0 divide-y">
                  {services.map((record) => (
                    <li key={record.id} className="grid gap-1 py-4 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-bold">{record.title}</span>
                        {record.previousOwner ? (
                          <Badge variant="outline">پیش از خرید شما</Badge>
                        ) : record.voided ? (
                          <span className="flex items-center gap-2">
                            <s className="text-sm font-black text-muted-foreground">
                              {faToman(record.voided.amount)}
                            </s>
                            <Badge variant="outline">باطل شده</Badge>
                          </span>
                        ) : (
                          <span className="text-sm font-black">{faToman(record.total)}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                        <span>{faDate(record.dateIso)}</span>
                        {record.branchName ? <span>{record.branchName}</span> : null}
                        <span>کارکرد {faKm(record.odometerKm)}</span>
                        {record.invoiceNumber ? <span>فاکتور {record.invoiceNumber}</span> : null}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// مدارک: کارت خودرو، بیمه، معاینه فنی
// ---------------------------------------------------------------------------

function DocumentCard({ ownedId, document }: { ownedId: string; document: VehicleDocument }) {
  const queryClient = useQueryClient();
  const removeMutation = useMutation(
    orpc.panel.documents.remove.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success("مدرک حذف شد");
          queryClient.invalidateQueries();
        } else {
          toast.error(result.reason);
        }
      },
    }),
  );

  return (
    <div
      className={cn(
        "grid gap-2 rounded-xl border p-4",
        document.state === "expired" && "border-destructive/40 bg-destructive/5",
        document.state === "ending" && "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-2 font-bold">
          <HugeiconsIcon
            icon={coreDocumentIcons.get(document.type) ?? Folder01Icon}
            strokeWidth={1.8}
            className="size-5 text-muted-foreground"
          />
          {document.typeLabel}
        </span>
        {documentStateBadge(document.state)}
      </div>
      <dl className="grid gap-1 text-sm text-muted-foreground">
        {document.number ? (
          <div className="flex items-center justify-between gap-2">
            <dt>شماره</dt>
            <dd className="font-bold text-foreground">{document.number}</dd>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          <dt>{document.expiryIso ? "اعتبار تا" : "تاریخ صدور"}</dt>
          <dd className="font-bold text-foreground">
            {faDate(document.expiryIso ?? document.issueIso)}
          </dd>
        </div>
      </dl>
      {document.note ? (
        <p className="text-xs leading-5 text-muted-foreground">{document.note}</p>
      ) : null}
      {document.state === "expired" || document.state === "ending" ? (
        <p className="text-xs font-bold text-destructive">
          {document.state === "expired"
            ? "برای جلوگیری از جریمه، تمدید را همین حالا انجام دهید."
            : "موعد تمدید نزدیک است؛ برنامه‌ریزی کنید."}
        </p>
      ) : null}
      <div className="flex items-center gap-1">
        <DocumentDialog ownedId={ownedId} document={document} />
        <AlertDialog>
          <AlertDialogTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-destructive"
                aria-label={`حذف ${document.typeLabel}`}
                disabled={removeMutation.isPending}
              />
            }
          >
            <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.8} />
          </AlertDialogTrigger>
          <AlertDialogContent lang="fa" dir="rtl">
            <AlertDialogHeader>
              <AlertDialogTitle>این مدرک حذف شود؟</AlertDialogTitle>
              <AlertDialogDescription>
                «{document.typeLabel}» از فهرست مدارک این خودرو برداشته می‌شود.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>انصراف</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  removeMutation.mutate({ ownedVehicleId: ownedId, documentId: document.id })
                }
              >
                بله، حذف شود
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

/**
 * انتخاب تاریخ داخل form.AppField؛ مقدار فیلد رشته ISO است و خالی یعنی انتخاب‌نشده.
 * past (مثل تاریخ صدور): روز آینده انتخاب‌شدنی نیست و تقویم تا امروز پیمایش می‌شود؛
 * بدون آن (انقضا) پیش‌فرض Calendar تا ده سال بعد است.
 */
function DateField({
  label,
  clearable,
  past,
}: {
  label: string;
  clearable?: boolean;
  past?: boolean;
}) {
  return (
    <FormField label={label}>
      {({ field, status, controlProps }) => {
        const value = String(field.state.value ?? "");
        const pick = (iso: string) => {
          field.handleChange(iso);
          field.handleBlur();
        };
        return (
          <div className="flex gap-2">
            <Popover
              onOpenChange={(next) => {
                if (!next) field.handleBlur();
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    id={controlProps.id}
                    aria-invalid={controlProps["aria-invalid"]}
                    aria-describedby={controlProps["aria-describedby"]}
                    className={cn("flex-1 justify-between", statusControlClass(status))}
                  />
                }
              >
                {value ? faDate(value) : "انتخاب تاریخ"}
                <HugeiconsIcon icon={Calendar03Icon} strokeWidth={1.8} />
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" lang="fa" dir="rtl">
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  selected={value ? new Date(value) : undefined}
                  onSelect={(next) => pick(next ? next.toISOString() : "")}
                  endMonth={past ? new Date() : undefined}
                  disabled={past ? { after: new Date() } : undefined}
                />
              </PopoverContent>
            </Popover>
            {clearable && value ? (
              <Button type="button" variant="ghost" onClick={() => pick("")}>
                بدون انقضا
              </Button>
            ) : null}
          </div>
        );
      }}
    </FormField>
  );
}

/** نوع مدرک در فهرست فعلی؛ نوعی که کارشناس بعداً برداشته، برای همان مدرک نگه داشته می‌شود */
const optionOf = (options: DocumentTypeOption[], type: string) =>
  options.find((option) => option.value === type);

/** سقف‌ها همان سقف سرور؛ انقضای لازم و ترتیب تاریخ‌ها را سرور هم (checkDocument) بررسی می‌کند */
const documentSchemaFor = (options: DocumentTypeOption[]) =>
  z
    .object({
      type: requiredChoice("نوع مدرک"),
      number: optionalText("شماره", 40),
      issueIso: requiredChoice("تاریخ صدور"),
      expiryIso: z.string(),
      note: optionalText("یادداشت", 200),
    })
    .superRefine((value, ctx) => {
      const expiry = optionOf(options, value.type)?.expiry ?? "optional";
      if (expiry === "none") return;
      if (expiry === "required" && !value.expiryIso) {
        ctx.addIssue({
          code: "custom",
          path: ["expiryIso"],
          message: "تاریخ انقضا را انتخاب کنید",
        });
      } else if (
        value.expiryIso &&
        value.issueIso &&
        new Date(value.expiryIso).getTime() <= new Date(value.issueIso).getTime()
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["expiryIso"],
          message: "تاریخ انقضا باید بعد از تاریخ صدور باشد",
        });
      }
    });

/** ثبت یا ویرایش یک مدرک؛ بدون document یعنی مدرک تازه */
function DocumentDialog({ ownedId, document }: { ownedId: string; document?: VehicleDocument }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const { panelDocuments } = useSiteContent();
  const listed = documentTypeOptions(panelDocuments.extraTypes);
  // نوعی که از فهرست برداشته شده، برای ویرایش همان مدرک در گزینه‌ها می‌ماند
  const options =
    document && !optionOf(listed, document.type)
      ? [
          ...listed,
          { value: document.type, label: document.typeLabel, expiry: "optional" as const },
        ]
      : listed;

  const onDone = (result: { ok: boolean; reason: string }) => {
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    toast.success(document ? "مدرک ویرایش شد" : "مدرک ثبت شد");
    queryClient.invalidateQueries();
    setOpen(false);
  };
  const addMutation = useMutation(orpc.panel.documents.add.mutationOptions({ onSuccess: onDone }));
  const updateMutation = useMutation(
    orpc.panel.documents.update.mutationOptions({ onSuccess: onDone }),
  );
  const pending = addMutation.isPending || updateMutation.isPending;

  const initial = {
    type: document?.type ?? "insurance",
    number: document?.number ?? "",
    issueIso: document?.issueIso ?? "",
    expiryIso: document?.expiryIso ?? "",
    note: document?.note ?? "",
  };

  const form = useAppForm({
    defaultValues: initial,
    validators: { onBlur: documentSchemaFor(options) },
    onSubmit: async ({ value }) => {
      setError("");
      const expiry = optionOf(options, value.type)?.expiry ?? "optional";
      const data = {
        type: value.type,
        number: value.number.trim(),
        issueIso: value.issueIso,
        // کارت خودرو و هر نوع بدون انقضا تاریخ انقضا نمی‌فرستد
        expiryIso: expiry === "none" || !value.expiryIso ? null : value.expiryIso,
        note: value.note.trim(),
      };
      await (
        document
          ? updateMutation.mutateAsync({ ownedVehicleId: ownedId, documentId: document.id, data })
          : addMutation.mutateAsync({ ownedVehicleId: ownedId, data })
      ).catch(() => undefined);
    },
  });
  const type = useStore(form.store, (state) => state.values.type);
  const expiry = optionOf(options, type)?.expiry ?? "optional";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // هر بار باز شدن، فرم از مقدار ذخیره‌شده مدرک (یا خالی برای مدرک تازه) شروع می‌کند
        if (next) {
          form.reset(initial);
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger
        render={
          document ? (
            <Button variant="ghost" size="icon-sm" aria-label="ویرایش مدرک" />
          ) : (
            <Button variant="outline" size="sm" />
          )
        }
      >
        <HugeiconsIcon icon={document ? Edit02Icon : PlusSignIcon} strokeWidth={1.8} />
        {document ? null : "ثبت مدرک"}
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>{document ? "ویرایش مدرک" : "ثبت مدرک جدید"}</DialogTitle>
              <DialogDescription>
                با ثبت تاریخ انقضا، پیش از پایان اعتبار در پنل یادآوری می‌گیرید؛ مدرک تا پایان همان
                روز معتبر است.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="type">
              {(field) => <field.SelectField label="نوع مدرک" options={options} />}
            </form.AppField>
            <form.AppField name="number">
              {(field) => <field.TextField label="شماره (اختیاری)" maxLength={40} />}
            </form.AppField>
            <form.AppField name="issueIso">
              {() => <DateField label="تاریخ صدور" past />}
            </form.AppField>
            {expiry !== "none" ? (
              <form.AppField name="expiryIso">
                {() => (
                  <DateField
                    label={expiry === "required" ? "تاریخ انقضا" : "تاریخ انقضا (اختیاری)"}
                    clearable={expiry === "optional"}
                  />
                )}
              </form.AppField>
            ) : null}
            <form.AppField name="note">
              {(field) => (
                <field.TextareaField
                  label="یادداشت (اختیاری)"
                  maxLength={200}
                  rows={2}
                  placeholder="مثلاً نام بیمه‌گر"
                />
              )}
            </form.AppField>
            <FormAlert message={error} />
            <DialogFooter>
              <form.SubmitButton pending={pending}>
                {document ? "ذخیره تغییرات" : "ثبت مدرک"}
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// فایل‌های مدارک
// ---------------------------------------------------------------------------

const documentFilePolicy = uploadPolicy("document-file");

/** A title from the file name when the customer leaves it empty (the server wants 2+ letters) */
const titleFromName = (name: string) => {
  const stem = name.replace(/\.[^.]+$/, "").trim();
  return (stem.length >= 2 ? stem : name).slice(0, 80);
};

/**
 * The file opens from a link signed for a few minutes, fetched on every click; a file
 * kept before object storage still arrives as a data URL, which browsers only open as a blob.
 */
async function openDocumentFile(ownedId: string, file: DocumentFile) {
  try {
    const stored = await client.panel.documents.readFile({
      ownedVehicleId: ownedId,
      fileId: file.id,
    });
    if (!stored) throw new Error("missing");
    if (!stored.url.startsWith("data:")) {
      window.open(stored.url, "_blank", "noopener");
      return;
    }
    const blob = await (await fetch(stored.url)).blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    toast.error("باز کردن فایل ممکن نشد");
  }
}

const fileTitleSchema = z.object({ title: optionalText("عنوان مدرک", 80) });

function VehicleFilesDialog({ ownedId, files }: { ownedId: string; files: DocumentFile[] }) {
  const queryClient = useQueryClient();
  const [openingId, setOpeningId] = useState("");
  // فقط عنوان فایل بعدی؛ ارسالی ندارد و هر فایل با پایان بارگذاری ذخیره می‌شود
  const titleForm = useAppForm({
    defaultValues: { title: "" },
    validators: { onBlur: fileTitleSchema },
  });

  // each file that reaches the private bucket is kept with the vehicle right away
  const uploads = useUploads({
    policy: documentFilePolicy,
    maxFiles: Math.max(0, documentFilesPerVehicle - files.length),
    removeDone: true,
    onUploaded: async (stored) => {
      const typed = titleForm.getFieldValue("title").trim();
      const result = await client.panel.documents.addFile({
        ownedVehicleId: ownedId,
        title: typed.length >= 2 ? typed : titleFromName(stored.name),
        storedFileId: stored.id,
      });
      if (!result.ok) throw new Error(result.reason);
      // the title belonged to this file; the next one starts from its own name
      if (typed) titleForm.reset();
      toast.success(`«${stored.name}» به مدارک اضافه شد`);
      await queryClient.invalidateQueries();
    },
  });
  const full = files.length >= documentFilesPerVehicle;

  const deleteMutation = useMutation(
    orpc.panel.documents.deleteFile.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success("فایل حذف شد");
          queryClient.invalidateQueries();
        } else {
          toast.error(result.reason);
        }
      },
    }),
  );

  const open = async (file: DocumentFile) => {
    setOpeningId(file.id);
    await openDocumentFile(ownedId, file);
    setOpeningId("");
  };

  return (
    <Dialog>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={Attachment01Icon} strokeWidth={1.8} />
        فایل‌ها
        {files.length > 0 ? ` (${faNumber(files.length)})` : ""}
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <DialogHeader>
          <DialogTitle>فایل‌های مدارک خودرو</DialogTitle>
          <DialogDescription>
            هر مدرک مرتبط را نگه دارید: گواهینامه، اسکن بیمه‌نامه، کارت خودرو، وکالت‌نامه و…؛ عکس یا
            PDF تا {formatBytes(documentFilePolicy.maxBytes)}، حداکثر{" "}
            {faNumber(documentFilesPerVehicle)} فایل برای هر خودرو. فایل‌ها خصوصی‌اند و فقط شما و
            کارشناسان تابان خودرو آن‌ها را می‌بینید.
          </DialogDescription>
        </DialogHeader>

        {files.length === 0 ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            هنوز فایلی بارگذاری نشده است.
          </p>
        ) : (
          <ul className="grid max-h-72 gap-2 overflow-y-auto">
            {files.map((file) => (
              <li key={file.id} className="flex items-center gap-3 rounded-lg border p-2.5">
                <span
                  className={cn(
                    "grid size-12 shrink-0 place-items-center rounded-md",
                    file.mimeType.startsWith("image/")
                      ? "bg-primary/10 text-primary"
                      : "bg-destructive/10 text-destructive",
                  )}
                >
                  <HugeiconsIcon
                    icon={file.mimeType.startsWith("image/") ? Image01Icon : Pdf01Icon}
                    strokeWidth={1.8}
                    className="size-6"
                  />
                </span>
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate text-sm font-bold">{file.title}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {file.name}
                    {SEP}
                    {formatBytes(file.size)}
                    {SEP}
                    {faDate(file.uploadedIso)}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`مشاهده ${file.title}`}
                  disabled={openingId === file.id}
                  onClick={() => open(file)}
                >
                  {openingId === file.id ? (
                    <Spinner className="size-4" />
                  ) : (
                    <HugeiconsIcon icon={ViewIcon} strokeWidth={1.8} />
                  )}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label={`حذف ${file.title}`}
                        disabled={deleteMutation.isPending}
                      />
                    }
                  >
                    <HugeiconsIcon icon={Delete02Icon} strokeWidth={1.8} />
                  </AlertDialogTrigger>
                  <AlertDialogContent lang="fa" dir="rtl">
                    <AlertDialogHeader>
                      <AlertDialogTitle>این فایل حذف شود؟</AlertDialogTitle>
                      <AlertDialogDescription>
                        «{file.title}» برای همیشه حذف می‌شود و قابل بازگردانی نیست.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>انصراف</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() =>
                          deleteMutation.mutate({ ownedVehicleId: ownedId, fileId: file.id })
                        }
                      >
                        بله، حذف شود
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 rounded-xl bg-muted/50 p-3">
          <titleForm.AppField name="title">
            {(field) => (
              <field.TextField
                label="عنوان مدرک جدید (اختیاری)"
                description="اگر عنوان خالی بماند، نام خود فایل استفاده می‌شود."
                placeholder="مثلاً گواهینامه رانندگی"
                maxLength={80}
                disabled={full}
              />
            )}
          </titleForm.AppField>
          {full ? (
            <p className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
              ظرفیت {faNumber(documentFilesPerVehicle)} فایل این خودرو پر است؛ برای افزودن، یکی از
              فایل‌ها را حذف کنید.
            </p>
          ) : (
            <FileDropzone
              uploads={uploads}
              multiple
              maxFiles={documentFilesPerVehicle - files.length}
              title="فایل مدرک را اینجا رها کنید"
              description="یا برای انتخاب از گوشی یا رایانه لمس کنید"
            />
          )}
          <UploadList uploads={uploads} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// کیلومتر
// ---------------------------------------------------------------------------

/** عددی که خود مشتری اشتباه زده، تا پیش از ثبت کیلومتر در نمایندگی پس گرفته می‌شود */
function RemoveReadingButton({
  ownedId,
  readingId,
  km,
}: {
  ownedId: string;
  readingId: string;
  km: number;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    orpc.panel.garage.removeReading.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success("عدد کیلومتر حذف شد");
          queryClient.invalidateQueries();
        } else {
          toast.error(result.reason);
        }
      },
    }),
  );
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button
            variant="link"
            size="sm"
            className="mt-1 h-auto p-0 text-xs text-destructive"
            disabled={mutation.isPending}
          />
        }
      >
        اشتباه زدم؛ حذف این عدد
      </AlertDialogTrigger>
      <AlertDialogContent lang="fa" dir="rtl">
        <AlertDialogHeader>
          <AlertDialogTitle>عدد {faKm(km)} حذف شود؟</AlertDialogTitle>
          <AlertDialogDescription>
            این عدد را خودتان ثبت کرده‌اید؛ پس از حذف، عدد درست را دوباره ثبت کنید.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>انصراف</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => mutation.mutate({ ownedVehicleId: ownedId, readingId })}
          >
            بله، حذف شود
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// سقف همان سقف ورودی سرور است؛ «بیشتر از آخرین عدد» را سرور با پیام خودش رد می‌کند
const readingSchema = z.object({
  km: intText("کیلومتر", { min: 1, max: 2_000_000, grouped: true }),
});

function AddReadingDialog({ ownedId, lastKm }: { ownedId: string; lastKm: number }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const mutation = useMutation(
    orpc.panel.garage.addReading.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success("کیلومتر جدید ثبت شد؛ تخمین‌ها به‌روز شدند");
          queryClient.invalidateQueries();
          setOpen(false);
        } else {
          setError(result.reason);
        }
      },
    }),
  );

  const form = useAppForm({
    defaultValues: { km: "" },
    validators: { onBlur: readingSchema },
    onSubmit: async ({ value }) => {
      const km = parseIntText(value.km);
      if (km === null) return;
      setError("");
      await mutation.mutateAsync({ ownedVehicleId: ownedId, km }).catch(() => undefined);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) form.reset({ km: "" });
        setOpen(next);
        setError("");
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
        ثبت کیلومتر
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>ثبت کیلومتر فعلی</DialogTitle>
              <DialogDescription>
                عدد روی صفحه‌کیلومتر را وارد کنید تا تخمین کارکرد و موعد سرویس‌ها دقیق‌تر شود. آخرین
                عدد ثبت‌شده {faKm(lastKm)} است.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              <form.AppField
                name="km"
                listeners={{
                  // پیام سرور مال عدد قبلی است؛ با تایپ دوباره پاک می‌شود
                  onChange: () => setError(""),
                }}
              >
                {(field) => (
                  <field.TextField
                    label="کیلومتر فعلی"
                    dir="ltr"
                    inputMode="numeric"
                    autoFocus
                    placeholder={faNumber(lastKm + 2_000)}
                    className="text-start"
                  />
                )}
              </form.AppField>
              <FormAlert message={error} />
            </div>
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>ثبت کیلومتر</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
